/**
 * Design Service - API calls for Design Assistant
 * Handles image generation for social media posts
 */

import { getStoredTokens, BASE_URL } from '../api/client';

// Design style categories and their metadata
export const DESIGN_CATEGORIES = {
    social: {
        name: 'Social Media',
        icon: 'Share2',
        color: 'sky',
        description: 'Graphics for social platforms'
    },
    ads: {
        name: 'Advertisements',
        icon: 'Megaphone',
        color: 'violet',
        description: 'Ad creatives and banners'
    },
    branding: {
        name: 'Branding',
        icon: 'Palette',
        color: 'amber',
        description: 'Logos and brand assets'
    },
    infographic: {
        name: 'Infographics',
        icon: 'BarChart3',
        color: 'emerald',
        description: 'Data visualizations'
    },
    presentation: {
        name: 'Presentations',
        icon: 'Presentation',
        color: 'rose',
        description: 'Slides and decks'
    }
};

// Image size presets
export const IMAGE_SIZES = {
    'instagram-post': { width: 1080, height: 1080, label: 'Instagram Post (1:1)' },
    'instagram-story': { width: 1080, height: 1920, label: 'Instagram Story (9:16)' },
    'facebook-post': { width: 1200, height: 630, label: 'Facebook Post (1.91:1)' },
    'twitter-post': { width: 1600, height: 900, label: 'Twitter/X Post (16:9)' },
    'linkedin-post': { width: 1200, height: 627, label: 'LinkedIn Post (1.91:1)' },
    'youtube-thumbnail': { width: 1280, height: 720, label: 'YouTube Thumbnail (16:9)' },
    'pinterest-pin': { width: 1000, height: 1500, label: 'Pinterest Pin (2:3)' },
    'custom': { width: 1024, height: 1024, label: 'Custom Size' }
};

// Style presets
export const STYLE_PRESETS = [
    { id: 'modern', name: 'Modern & Clean', description: 'Minimalist, professional look' },
    { id: 'bold', name: 'Bold & Vibrant', description: 'Eye-catching colors and typography' },
    { id: 'elegant', name: 'Elegant & Luxurious', description: 'Sophisticated, premium feel' },
    { id: 'playful', name: 'Playful & Fun', description: 'Bright, energetic designs' },
    { id: 'corporate', name: 'Corporate & Professional', description: 'Business-appropriate styling' },
    { id: 'vintage', name: 'Vintage & Retro', description: 'Classic, nostalgic aesthetic' },
    { id: 'minimalist', name: 'Minimalist', description: 'Simple, focused designs' },
    { id: 'creative', name: 'Creative & Artistic', description: 'Unique, artistic approach' }
];

/**
 * Generate a unique session ID for design conversations
 */
export function generateDesignSessionId() {
    return `design_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Check if user has access to Design Assistant
 */
export function hasDesignAccess(subscription) {
    if (!subscription) return false;
    const tier = subscription?.tier?.toLowerCase() ||
                 subscription?.plan?.tier?.toLowerCase() ||
                 subscription?.plan?.name?.toLowerCase() ||
                 'free';
    return ['ultra', 'enterprise'].includes(tier);
}

/**
 * Generate an image based on the prompt and settings
 * Uses streaming for progress updates
 */
export async function generateImage(prompt, options = {}, onProgress) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const {
        size = 'instagram-post',
        style = 'modern',
        category = 'social',
        customWidth,
        customHeight
    } = options;

    const sizeConfig = IMAGE_SIZES[size] || IMAGE_SIZES['instagram-post'];
    const width = size === 'custom' ? (customWidth || 1024) : sizeConfig.width;
    const height = size === 'custom' ? (customHeight || 1024) : sizeConfig.height;

    const response = await fetch(`${BASE_URL}/api/design/generate`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
            prompt,
            width,
            height,
            style,
            category,
            aspect_ratio: `${width}:${height}`
        })
    });

    if (!response.ok) {
        if (response.status === 402) {
            const error = new Error('Insufficient credits');
            error.code = 'INSUFFICIENT_CREDITS';
            throw error;
        }
        if (response.status === 401) {
            throw new Error('Authentication expired. Please sign in again.');
        }
        throw new Error(`Image generation failed: ${response.status}`);
    }

    // Handle streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let result = null;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
            if (line.startsWith('data: ')) {
                try {
                    const data = JSON.parse(line.slice(6));

                    if (data.type === 'progress' && onProgress) {
                        onProgress(data.progress, data.message);
                    } else if (data.type === 'complete') {
                        result = data;
                    } else if (data.type === 'error') {
                        throw new Error(data.message || 'Image generation failed');
                    }
                } catch (e) {
                    if (e.message !== 'Image generation failed') {
                        // JSON parse error, ignore
                    } else {
                        throw e;
                    }
                }
            }
        }
    }

    return result;
}

/**
 * Send a design chat message (for conversational design assistance)
 * Supports streaming responses
 */
export async function sendDesignMessage(message, sessionId, options = {}, callbacks = {}) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const { onChunk, onThinkingStep, onComplete, onError, onCreditInfo } = callbacks;

    const response = await fetch(`${BASE_URL}/api/chat/v2/ask/stream`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'text/event-stream'
        },
        body: JSON.stringify({
            message,
            sessionId,
            mode: 'design',
            context: {
                category: options.category || 'social',
                style: options.style || 'modern',
                size: options.size || 'instagram-post',
                ...options.context
            }
        })
    });

    if (!response.ok) {
        if (response.status === 402) {
            const errorData = await response.json().catch(() => ({}));
            const error = new Error('Insufficient credits');
            error.code = 'INSUFFICIENT_CREDITS';
            error.details = errorData;
            if (onError) onError(error);
            throw error;
        }
        if (response.status === 401) {
            const error = new Error('Authentication expired');
            if (onError) onError(error);
            throw error;
        }
        const error = new Error(`Request failed: ${response.status}`);
        if (onError) onError(error);
        throw error;
    }

    // Process streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let fullResponse = '';
    let imageUrl = null;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
            if (!line.startsWith('data: ')) continue;

            try {
                const data = JSON.parse(line.slice(6));

                switch (data.type) {
                    case 'credit_info':
                        if (onCreditInfo) onCreditInfo(data);
                        break;
                    case 'thinking_step':
                        if (onThinkingStep) onThinkingStep(data);
                        break;
                    case 'chunk':
                        fullResponse += data.content || '';
                        if (onChunk) onChunk(data.content || '');
                        break;
                    case 'image':
                        imageUrl = data.url;
                        if (onChunk) onChunk(`\n![Generated Image](${data.url})\n`);
                        break;
                    case 'done':
                        if (onComplete) onComplete({ content: fullResponse, imageUrl });
                        break;
                    case 'error':
                        const error = new Error(data.message || 'An error occurred');
                        if (onError) onError(error);
                        throw error;
                    default:
                        break;
                }
            } catch (e) {
                // Ignore JSON parse errors for incomplete chunks
            }
        }
    }

    return { content: fullResponse, imageUrl };
}

/**
 * Fetch design session history
 */
export async function fetchDesignSessions() {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/chat/v2/history/sessions?mode=design`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch sessions: ${response.status}`);
    }

    return response.json();
}

/**
 * Load a specific design session
 */
export async function loadDesignSession(sessionId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/chat/v2/history/sessions/${sessionId}`, {
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`,
            'Accept': 'application/json'
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to load session: ${response.status}`);
    }

    return response.json();
}

/**
 * Delete a design session
 */
export async function deleteDesignSession(sessionId) {
    const tokens = getStoredTokens();
    if (!tokens?.accessToken) {
        throw new Error('Authentication required');
    }

    const response = await fetch(`${BASE_URL}/api/chat/v2/history/sessions/${sessionId}`, {
        method: 'DELETE',
        headers: {
            'Authorization': `Bearer ${tokens.accessToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Failed to delete session: ${response.status}`);
    }

    return true;
}

/**
 * Get suggested prompts for design generation
 */
export function getDesignPromptSuggestions(category = 'social') {
    const suggestions = {
        social: [
            'Create an Instagram post announcing our new mortgage rates',
            'Design a LinkedIn carousel about home buying tips',
            'Make a Facebook ad for first-time homebuyers',
            'Create a story graphic for our refinancing services'
        ],
        ads: [
            'Design a banner ad for our low-rate mortgage offer',
            'Create a retargeting ad for website visitors',
            'Make a Google Display ad for home equity loans',
            'Design an email header for our newsletter'
        ],
        branding: [
            'Create a professional logo mockup',
            'Design a business card layout',
            'Make a branded social media template',
            'Create a brand color palette visualization'
        ],
        infographic: [
            'Create an infographic about the home buying process',
            'Design a chart showing mortgage rate trends',
            'Make a visual guide to loan types',
            'Create a timeline of the closing process'
        ],
        presentation: [
            'Design a slide for our company overview',
            'Create a chart slide for quarterly results',
            'Make a team introduction slide',
            'Design a process flow diagram slide'
        ]
    };

    return suggestions[category] || suggestions.social;
}
